import React, { useState, useEffect } from 'react';
import StockSearch from '../../components/Research/StockSearch';
import TradeModal from '../../components/Research/TradeModal';
import CompanyProfileModal from '../../components/Research/CompanyProfileModal';
import { tradingAPI, handleAPIError, handleAPISuccess } from '../../services/api';
import toast from 'react-hot-toast';
import { 
  Search, 
  Star, 
  Plus, 
  Edit3, 
  Trash2, 
  X, 
  AlertTriangle,
  BarChart3
} from 'lucide-react';

function Modal({ open, title, children, onClose }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 min-w-[400px] max-w-md w-full relative border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">{title}</h2>
          <button 
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors duration-200" 
            onClick={onClose}
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const Research = ({ onTradeComplete }) => {
  const [watchlists, setWatchlists] = useState([]);
  const [selectedWatchlistId, setSelectedWatchlistId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Modal state
  const [modal, setModal] = useState({ open: false, type: '', watchlistId: null });
  const [modalInput, setModalInput] = useState('');
  const [confirm, setConfirm] = useState({ open: false, type: '', watchlistId: null });
  const [watchlistActionLoading, setWatchlistActionLoading] = useState(false);
  
  // Modal states for trade and profile
  const [tradeSymbol, setTradeSymbol] = useState(null);
  const [companyProfileSymbol, setCompanyProfileSymbol] = useState(null);

  // Removed FTSE/Volume market summary cards and related polling logic

  // Fetch all watchlists on mount
  useEffect(() => {
    fetchWatchlists();
  }, []);

  const fetchWatchlists = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await tradingAPI.getWatchlists();
      // Support both paginated and array responses
      const data = Array.isArray(res.data) ? res.data : (res.data.results || []);
      setWatchlists(data);
      if (data.length > 0 && !selectedWatchlistId) {
        setSelectedWatchlistId(data[0].id);
      }
    } catch (err) {
      setError(handleAPIError(err, 'Failed to load watchlists.'));
    } finally {
      setLoading(false);
    } 
  };

  const selectedWatchlist = watchlists.find(w => w.id === selectedWatchlistId);

  // Add/remove/clear stock actions
  const addToWatchlist = async (symbol) => {
    if (!selectedWatchlistId) return;
    setWatchlistActionLoading(true);
    setError('');
    try {
      const response = await tradingAPI.addToWatchlist(selectedWatchlistId, symbol);
      await fetchWatchlists();
      
      // Show success toast
      const watchlistName = watchlists.find(w => w.id === selectedWatchlistId)?.name || 'watchlist';
      toast.success(`${symbol} added to ${watchlistName} successfully!`);
    } catch (err) {
      setError(handleAPIError(err, 'Failed to add to watchlist.'));
    } finally {
      setWatchlistActionLoading(false);
    }
  };
  const removeFromWatchlist = async (symbol) => {
    if (!selectedWatchlistId) return;
    setWatchlistActionLoading(true);
    setError('');
    try {
      await tradingAPI.removeFromWatchlist(selectedWatchlistId, symbol);
      await fetchWatchlists();
      
      // Show success toast
      const watchlistName = watchlists.find(w => w.id === selectedWatchlistId)?.name || 'watchlist';
      toast.success(`${symbol} removed from ${watchlistName} successfully!`);
    } catch (err) {
      setError(handleAPIError(err, 'Failed to remove from watchlist.'));
    } finally {
      setWatchlistActionLoading(false);
    }
  };
  const clearWatchlist = async () => {
    if (!selectedWatchlistId) return;
    if (!window.confirm('Clear all stocks from this watchlist?')) return;
    setError('');
    try {
      await tradingAPI.clearWatchlist(selectedWatchlistId);
      await fetchWatchlists();
      
      // Show success toast
      const watchlistName = watchlists.find(w => w.id === selectedWatchlistId)?.name || 'watchlist';
      toast.success(`${watchlistName} cleared successfully!`);
    } catch (err) {
      setError(handleAPIError(err, 'Failed to clear watchlist.'));
    }
  };

  // Modal handlers
  const openAddModal = () => {
    setModal({ open: true, type: 'add', watchlistId: null });
    setModalInput('');
  };
  const openRenameModal = (id, currentName) => {
    setModal({ open: true, type: 'rename', watchlistId: id });
    setModalInput(currentName || '');
  };
  const openConfirm = (type, id) => {
    setConfirm({ open: true, type, watchlistId: id });
  };
  const closeModal = () => setModal({ open: false, type: '', watchlistId: null });
  const closeConfirm = () => setConfirm({ open: false, type: '', watchlistId: null });

  // Modal submit
  const handleModalSubmit = async () => {
    if (!modalInput.trim()) return;
    setError('');
    
    if (modal.type === 'add') {
      try {
        await tradingAPI.createWatchlist({ name: modalInput.trim() });
        await fetchWatchlists();
        toast.success(`Watchlist "${modalInput.trim()}" created successfully!`);
        closeModal();
      } catch (err) {
        setError(handleAPIError(err, 'Failed to create watchlist.'));
      }
    } else if (modal.type === 'rename') {
      try {
        await tradingAPI.renameWatchlist(modal.watchlistId, modalInput.trim());
        await fetchWatchlists();
        toast.success(`Watchlist renamed to "${modalInput.trim()}" successfully!`);
        closeModal();
      } catch (err) {
        setError(handleAPIError(err, 'Failed to rename watchlist.'));
      }
    }
  };

  // Confirm submit
  const handleConfirmSubmit = async () => {
    setError('');
    
    if (confirm.type === 'delete') {
      try {
        const watchlistName = watchlists.find(w => w.id === confirm.watchlistId)?.name || 'watchlist';
        await tradingAPI.deleteWatchlist(confirm.watchlistId);
        await fetchWatchlists();
        setSelectedWatchlistId((prev) => {
          const remaining = watchlists.filter(w => w.id !== confirm.watchlistId);
          return remaining.length > 0 ? remaining[0].id : null;
        });
        toast.success(`Watchlist "${watchlistName}" deleted successfully!`);
        closeConfirm();
      } catch (err) {
        setError(handleAPIError(err, 'Failed to delete watchlist.'));
      }
    } else if (confirm.type === 'clear') {
      try {
        const watchlistName = watchlists.find(w => w.id === confirm.watchlistId)?.name || 'watchlist';
        await tradingAPI.clearWatchlist(confirm.watchlistId);
        await fetchWatchlists();
        toast.success(`Watchlist "${watchlistName}" cleared successfully!`);
        closeConfirm();
      } catch (err) {
        setError(handleAPIError(err, 'Failed to clear watchlist.'));
      }
    }
  };







  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-center items-center h-96">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-400 font-medium">Loading research tools...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6">
            <div className="flex items-center space-x-3">
              <AlertTriangle className="h-6 w-6 text-red-600" />
              <div>
                <h3 className="text-lg font-semibold text-red-800 dark:text-red-200">Error Loading Research</h3>
                <p className="text-red-700 dark:text-red-300 mt-1">{error}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-slate-900 dark:to-indigo-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Error Message */}
        {error && (
          <div className="mb-6">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
              <div className="flex items-center space-x-3">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                <p className="text-red-700 dark:text-red-300 font-medium">{error}</p>
              </div>
            </div>
          </div>
        )}
        {/* Killer Professional Header */}
        <div className="mb-8">
          <div className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 rounded-3xl shadow-2xl border border-blue-500/20">
            {/* Animated Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-blue-600/20 via-transparent to-purple-600/20"></div>
              <div className="absolute top-10 left-10 w-32 h-32 bg-blue-500/10 rounded-full blur-xl animate-pulse"></div>
              <div className="absolute bottom-10 right-10 w-24 h-24 bg-purple-500/10 rounded-full blur-xl animate-pulse delay-1000"></div>
              <div className="absolute top-1/2 left-1/2 w-16 h-16 bg-indigo-500/10 rounded-full blur-lg animate-pulse delay-500"></div>
            </div>
            
            <div className="relative p-8">
              <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-6">
                {/* Left side - Premium Title */}
                <div className="flex items-center space-x-6">
                  <div className="p-4 bg-gradient-to-br from-blue-500 via-indigo-600 to-purple-600 rounded-2xl shadow-xl">
                    <BarChart3 className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <h1 className="text-4xl font-black bg-gradient-to-r from-white via-blue-100 to-indigo-200 bg-clip-text text-transparent mb-2">
                      Market Research
                    </h1>
                    <p className="text-lg font-medium text-blue-100/90">
                      Professional stock analysis & real-time market intelligence
                    </p>
                  </div>
                </div>

                {/* Right side - Market summary cards removed */}
              </div>
            </div>
          </div>
        </div>

        {/* Header Section */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Stock Research & Watchlist</h2>
            <p className="text-gray-600 dark:text-gray-300">Search for UK companies and manage your watchlists</p>
          </div>
          
          {/* Watchlist Management */}
          <div className="flex items-center gap-3">
            {watchlists.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-600 dark:text-gray-300">Active Watchlist:</span>
                <select
                  value={selectedWatchlistId || ''}
                  onChange={(e) => setSelectedWatchlistId(e.target.value ? parseInt(e.target.value) : null)}
                  className="px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select Watchlist</option>
                  {watchlists.map(w => (
                    <option key={w.id} value={w.id}>
                      {w.name} ({w.stocks?.length || 0})
                    </option>
                  ))}
                </select>
              </div>
            )}
            <button 
              onClick={openAddModal}
              className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all duration-300 text-sm font-semibold shadow-lg hover:shadow-xl"
            >
              <Plus className="h-4 w-4" />
              <span>New Watchlist</span>
            </button>
          </div>
        </div>

        {/* Watchlist Actions */}
        {selectedWatchlist && (
          <div className="flex flex-wrap items-center gap-3 p-4 bg-gray-50/80 dark:bg-gray-700/50 rounded-xl mb-6">
            <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
              Managing: <span className="font-semibold text-gray-900 dark:text-white">{selectedWatchlist.name}</span>
            </span>
            <div className="flex items-center space-x-2 ml-auto">
              <button 
                onClick={() => openRenameModal(selectedWatchlist.id, selectedWatchlist.name)}
                className="flex items-center space-x-2 px-3 py-2 bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-500 transition-all duration-200 text-sm font-medium shadow-sm hover:shadow-md"
              >
                <Edit3 className="h-4 w-4" />
                <span>Rename</span>
              </button>
              <button 
                onClick={() => openConfirm('clear', selectedWatchlist.id)}
                className="flex items-center space-x-2 px-3 py-2 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-lg hover:bg-orange-200 dark:hover:bg-orange-900/50 transition-all duration-200 text-sm font-medium shadow-sm hover:shadow-md"
              >
                <X className="h-4 w-4" />
                <span>Clear</span>
              </button>
              <button 
                onClick={() => openConfirm('delete', selectedWatchlist.id)}
                className="flex items-center space-x-2 px-3 py-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition-all duration-200 text-sm font-medium shadow-sm hover:shadow-md"
              >
                <Trash2 className="h-4 w-4" />
                <span>Delete</span>
              </button>
            </div>
          </div>
        )}

        {/* Main Search and Results */}
        <StockSearch
          onTradeComplete={onTradeComplete}
          watchlist={selectedWatchlist ? selectedWatchlist.stocks.map(s => s.symbol) : []}
          toggleWatchlist={(symbol) => {
            if (!selectedWatchlist) return;
            const isIn = selectedWatchlist.stocks.some(s => s.symbol === symbol);
            isIn ? removeFromWatchlist(symbol) : addToWatchlist(symbol);
          }}
          watchlistActionLoading={watchlistActionLoading}
        />

        {/* Watchlist Display Card */}
        {selectedWatchlist && selectedWatchlist.stocks && selectedWatchlist.stocks.length > 0 && (
          <div className="mt-8">
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-xl border border-amber-200/50 dark:border-amber-700/50 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg">
                    <Star className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                      {selectedWatchlist.name} Watchlist
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {selectedWatchlist.stocks.length} stock{selectedWatchlist.stocks.length !== 1 ? 's' : ''} in your watchlist
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => openConfirm('clear', selectedWatchlist.id)}
                  className="flex items-center space-x-2 px-3 py-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/50 transition-all duration-200 text-sm font-medium"
                >
                  <X className="h-4 w-4" />
                  <span>Clear All</span>
                </button>
              </div>
              
              {/* Watchlist Stocks Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {selectedWatchlist.stocks.map((stock, index) => (
                  <div key={stock.symbol} className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm p-4 rounded-lg border border-gray-200/50 dark:border-gray-700/50 hover:shadow-md transition-all duration-200">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-gray-900 dark:text-white text-sm">
                        {stock.symbol}
                      </h4>
                      <button
                        onClick={() => removeFromWatchlist(stock.symbol)}
                        className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                        title="Remove from watchlist"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-300 mb-2">
                      {stock.name || 'Loading...'}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 px-2 py-1 rounded-full">
                        #{index + 1}
                      </span>
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => setTradeSymbol(stock.symbol)}
                          className="text-xs bg-green-500 hover:bg-green-600 text-white px-1.5 py-1 rounded transition-colors flex-shrink-0"
                        >
                          Trade
                        </button>
                        <button
                          onClick={() => setCompanyProfileSymbol(stock.symbol)}
                          className="text-xs bg-blue-500 hover:bg-blue-600 text-white px-1.5 py-1 rounded transition-colors flex-shrink-0"
                        >
                          Profile
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Empty Watchlist State */}
        {selectedWatchlist && (!selectedWatchlist.stocks || selectedWatchlist.stocks.length === 0) && (
          <div className="mt-8">
            <div className="bg-gray-50/80 dark:bg-gray-700/50 rounded-xl border border-gray-200/50 dark:border-gray-600/50 p-8 text-center">
              <div className="p-4 bg-gray-100 dark:bg-gray-600 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                <Star className="h-8 w-8 text-gray-400" />
              </div>
              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {selectedWatchlist.name} is Empty
              </h4>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Start adding stocks to your watchlist by searching and clicking the star icon.
              </p>
            </div>
          </div>
        )}

        {/* Modal for add/rename */}
        <Modal open={modal.open} title={modal.type === 'add' ? 'Create New Watchlist' : 'Rename Watchlist'} onClose={closeModal}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {modal.type === 'add' ? 'Watchlist Name' : 'New Name'}
              </label>
              <input
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                placeholder={modal.type === 'add' ? 'Enter watchlist name' : 'Enter new name'}
                value={modalInput}
                onChange={e => setModalInput(e.target.value)}
                autoFocus
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button 
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200" 
                onClick={closeModal}
              >
                Cancel
              </button>
              <button 
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors duration-200" 
                onClick={handleModalSubmit}
              >
                {modal.type === 'add' ? 'Create' : 'Rename'}
              </button>
            </div>
          </div>
        </Modal>

        {/* Confirm dialog for delete/clear */}
        <Modal open={confirm.open} title={confirm.type === 'delete' ? 'Delete Watchlist' : 'Clear Watchlist'} onClose={closeConfirm}>
          <div className="space-y-4">
            <div className="flex items-center space-x-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              <p className="text-sm text-red-800 dark:text-red-200">
                {confirm.type === 'delete'
                  ? 'Are you sure you want to delete this watchlist? This action cannot be undone.'
                  : 'Are you sure you want to remove all stocks from this watchlist?'}
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button 
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200" 
                onClick={closeConfirm}
              >
                Cancel
              </button>
              <button 
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors duration-200" 
                onClick={handleConfirmSubmit}
              >
                Yes, {confirm.type === 'delete' ? 'Delete' : 'Clear'}
              </button>
            </div>
          </div>
        </Modal>

        {/* Trade and Profile Modals */}
        <TradeModal
          symbol={tradeSymbol}
          onClose={() => setTradeSymbol(null)}
          onTradeComplete={onTradeComplete}
        />
        <CompanyProfileModal 
          symbol={companyProfileSymbol} 
          onClose={() => setCompanyProfileSymbol(null)} 
        />
      </div>
    </div>
  );
};

export default Research; 